import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  LinearProgress,
  Typography,
} from '@mui/material';
import { GetApp } from '@mui/icons-material';
import { ExportOptions, UIExportConfig } from './ExportOptions';
import { exportService } from '../../services/exportService';
import { useAuth } from '../../context/AuthContext';
import { ExportConfig } from '../../types/analysis';

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  deckId: string;
  deckName: string;
}

export const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  deckId,
  deckName,
}) => {
  const { token } = useAuth();
  const defaultUiConfig: UIExportConfig = {
    format: 'pdf',
    includeSummary: true,
    includeFindings: true,
    includeSlideImages: true,
    severityFilter: ['critical', 'high', 'low'],
  };
  const [uiConfig, setUiConfig] = useState<UIExportConfig>({
    ...defaultUiConfig,
  });
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  // Reset dialog state each time it is opened
  useEffect(() => {
    if (open) {
      setUiConfig({ ...defaultUiConfig });
      setExporting(false);
      setProgress(0);
      setExportStatus('');
      setError(null);
      setDownloadUrl(null);
    }
  }, [open]);

  const handleExport = async () => {
    if (!token) return;

    setExporting(true);
    setError(null);
    setProgress(0);
    setExportStatus('Initiating export...');

    try {
      // Map UI config to API config
      const apiConfig: ExportConfig = {
        format: uiConfig.format,
        includeSlides: 'all',
        severityFilter: uiConfig.severityFilter as ('critical' | 'high' | 'low')[],
        includeSlideImages: uiConfig.includeSlideImages,
      };

      // Initiate export (returns immediately with reportId)
      setExportStatus('Starting report generation...');
      const initialResult = await exportService.exportReport(
        deckId,
        apiConfig,
        token
      );

      // Poll for completion with progress updates
      setExportStatus('Generating report...');
      const completedResult = await exportService.pollExportStatus(
        initialResult.reportId,
        token,
        (currentProgress) => {
          setProgress(currentProgress);
          if (currentProgress < 40) {
            setExportStatus('Loading deck data...');
          } else if (currentProgress < 70) {
            setExportStatus('Generating report...');
          } else if (currentProgress < 90) {
            setExportStatus('Uploading to storage...');
          } else {
            setExportStatus('Finalizing...');
          }
        }
      );

      // Expose a clickable link to avoid popup blockers
      if (completedResult.downloadUrl) {
        setExportStatus('Report is ready to download');
        setProgress(100);
        setDownloadUrl(completedResult.downloadUrl);
        setExporting(false);
      }
    } catch (err) {
      setError((err as Error).message);
      setExporting(false);
    }
  };

  const handleClose = () => {
    if (!exporting) {
      onClose();
    }
  };

  const handleDownloadClick = () => {
    if (downloadUrl) {
      exportService.downloadReport(downloadUrl);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Export Analysis Report</DialogTitle>
      
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {exporting && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              {exportStatus}
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              {progress}% complete
            </Typography>
          </Alert>
        )}

        {!exporting && downloadUrl && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Report generated successfully.
            </Typography>
            <Button variant="contained" onClick={handleDownloadClick} startIcon={<GetApp />}>
              Download report
            </Button>
          </Alert>
        )}

        <ExportOptions config={uiConfig} onChange={setUiConfig} />
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={exporting}>
          Cancel
        </Button>
        {!downloadUrl && (
          <Button
            onClick={handleExport}
            disabled={exporting || uiConfig.severityFilter.length === 0}
            variant="contained"
            startIcon={<GetApp />}
          >
            Export
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

